var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
/*
 * Copyright (c) 2019-present Sonatype, Inc.
 * This program and the accompanying materials are made available under
 * the terms of the Eclipse Public License 2.0 which accompanies this
 * distribution and is available at https://www.eclipse.org/legal/epl-2.0/.
 */
import React, { useMemo } from 'react';
import classnames from 'classnames';
import { chain, groupBy, reject, without } from 'ramda';
import { propTypes } from './types';
import TransferListHalf from './TransferListHalf';
import './NxTransferList.scss';
var defaultItemsCountFormatter = function (kind) { return function (n) { return n + " item" + (n === 1 ? '' : 's') + " " + kind; }; }, defaultAvailableItemsCountFormatter = defaultItemsCountFormatter('available'), defaultSelectedItemsCountFormatter = defaultItemsCountFormatter('transferred');
export default function NxTransferList(props) {
    var _a = props.allowReordering, allowReordering = _a === void 0 ? false : _a, allItems = props.allItems, selectedItems = props.selectedItems, availableItemsLabel = props.availableItemsLabel, selectedItemsLabel = props.selectedItemsLabel, showMoveAll = props.showMoveAll, availableItemsFilter = props.availableItemsFilter, selectedItemsFilter = props.selectedItemsFilter, availableItemsCountFormatterProp = props.availableItemsCountFormatter, selectedItemsCountFormatterProp = props.selectedItemsCountFormatter, onAvailableItemsFilterChange = props.onAvailableItemsFilterChange, onSelectedItemsFilterChange = props.onSelectedItemsFilterChange, classNameProp = props.className, onChangeProp = props.onChange, filterFn = props.filterFn, attrs = __rest(props, ["allowReordering", "allItems", "selectedItems", "availableItemsLabel", "selectedItemsLabel", "showMoveAll", "availableItemsFilter", "selectedItemsFilter", "availableItemsCountFormatter", "selectedItemsCountFormatter", "onAvailableItemsFilterChange", "onSelectedItemsFilterChange", "className", "onChange", "filterFn"]);
    if (allowReordering && !Array.isArray(selectedItems)) {
        throw new TypeError('selectedItems must be an array if allowReordering is true');
    }
    var selectedItemsArray = Array.from(selectedItems);
    var selectedItemsSet = selectedItems instanceof Set ? selectedItems : new Set(selectedItemsArray);
    var availableItemsCountFormatter = availableItemsCountFormatterProp || defaultAvailableItemsCountFormatter, selectedItemsCountFormatter = selectedItemsCountFormatterProp || defaultSelectedItemsCountFormatter;
    var groupedItems = useMemo(function () {
        var allItemsIdToItemLookUp = groupBy(function (item) { return item.id.toString(); }, allItems);
        return allowReordering
            ? {
                available: reject(function (item) { return selectedItemsSet.has(item.id); }, allItems),
                selected: chain(function (item) { return allItemsIdToItemLookUp[item.toString()]; }, selectedItemsArray)
            }
            : groupBy(function (item) { return selectedItemsSet.has(item.id) ? 'selected' : 'available'; }, allItems);
    }, [allItems, selectedItems, allowReordering]), available = groupedItems.available || [], selected = groupedItems.selected || [];
    var availableCount = allItems.length - selectedItemsArray.length, selectedCount = selectedItemsArray.length;
    var handleOnChangeProp = function (array) {
        if (allowReordering) {
            onChangeProp(array);
        }
        else {
            onChangeProp(new Set(array));
        }
    };
    function onChange(checked, id) {
        var newSelectedItemsArray = checked
            ? __spreadArrays(selectedItemsArray, [id]) : without([id], selectedItemsArray);
        handleOnChangeProp(newSelectedItemsArray);
    }
    function onSelectAll(idsToAdd) {
        handleOnChangeProp(__spreadArrays(selectedItemsArray, idsToAdd));
    }
    function onUnselectAll(idsToRemove) {
        handleOnChangeProp(without(idsToRemove, selectedItemsArray));
    }
    function onReorderItem(index, direction) {
        if (typeof selectedItemsArray[index + direction] === 'undefined') {
            return;
        }
        var newSelectedItems = __spreadArrays(selectedItemsArray);
        newSelectedItems[index] = selectedItemsArray[index + direction];
        newSelectedItems[index + direction] = selectedItemsArray[index];
        handleOnChangeProp(newSelectedItems);
    }
    return (React.createElement("div", __assign({ className: classnames('nx-transfer-list', classNameProp) }, attrs),
        React.createElement(TransferListHalf, { label: availableItemsLabel || 'Available Items', filterValue: availableItemsFilter, onFilterChange: onAvailableItemsFilterChange, showMoveAll: showMoveAll || false, onMoveAll: onSelectAll, isSelected: false, items: available, onItemChange: onChange, onReorderItem: onReorderItem, footerContent: availableItemsCountFormatter(availableCount), filterFn: filterFn, allowReordering: false }),
        React.createElement(TransferListHalf, { label: selectedItemsLabel || 'Transferred Items', filterValue: selectedItemsFilter, onFilterChange: onSelectedItemsFilterChange, showMoveAll: showMoveAll || false, onMoveAll: onUnselectAll, isSelected: true, items: selected, onItemChange: onChange, onReorderItem: onReorderItem, footerContent: selectedItemsCountFormatter(selectedCount), filterFn: filterFn, allowReordering: allowReordering })));
}
NxTransferList.propTypes = propTypes;
//# sourceMappingURL=NxTransferList.js.map